import { enqueueOperation, type MutationOperation } from './outbox';
import type { OutboxOperation } from './records';
import { requireTenantDb, type TenantDatabase } from './tenant-db';

export interface ApplyMutationInput {
    /**
     * De bewerkingen voor de outbox, in de vorm van het draadformaat. Leeg voor
     * een lokale wijziging zonder draadvorm: een foto (die gaat via
     * `POST /media`, niet via een batch) of het NTA 8800-concept.
     */
    operations?: readonly MutationOperation[];
    /**
     * Past de wijziging lokaal toe. Draait in dezelfde transactie als het
     * vullen van de outbox, zodat de UI en de queue nooit uit elkaar lopen.
     * Alleen Dexie-werk: een andere `await` laat de transactie voortijdig
     * committen.
     *
     * Hangt de bewerking af van wat er lokaal staat (bestaat dit antwoord al,
     * welk `answer_uuid` heeft het), lees dat dan híér en geef de bewerkingen
     * terug: dan kunnen twee snelle tikken elkaar niet inhalen.
     */
    apply?: (
        db: TenantDatabase,
        clientUpdatedAt: string,
    ) => Promise<readonly MutationOperation[] | void> | readonly MutationOperation[] | void;
}

type Flusher = () => void;

let flusher: Flusher | null = null;

/** De `SyncProvider` meldt hier zijn engine aan, zodat een mutatie direct kan synchroniseren. */
export function setMutationFlusher(next: Flusher | null): void {
    flusher = next;
}

/**
 * `2026-09-16T10:14:22.123+02:00`: ISO 8601 met milliseconden en de offset van
 * het apparaat, zoals `client_updated_at` in het draadformaat.
 */
export function isoWithOffset(epochMs: number): string {
    const date = new Date(epochMs);
    const offset = -date.getTimezoneOffset();
    const pad = (value: number, length = 2) => String(Math.abs(value)).padStart(length, '0');

    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
        `.${pad(date.getMilliseconds(), 3)}` +
        `${offset >= 0 ? '+' : '-'}${pad(Math.trunc(offset / 60))}:${pad(offset % 60)}`
    );
}

/**
 * Het tijdstip van een wijziging op iets waarvan het apparaat al een versie kent:
 * nooit eerder dan die versie, ook niet als de eigen klok achterloopt.
 *
 * De server beslist per antwoord, aantal en bevinding op `client_updated_at`
 * wie wint. Wie de versie van een ander apparaat gezien heeft en hem daarna
 * wijzigt, wijzigt hem echt later; loopt de klok van dit apparaat een paar
 * minuten achter op die van kantoor, dan zou de oude versie anders winnen en
 * draaide de server de wijziging terug. Een echte gelijktijdige wijziging (die
 * het apparaat niet gezien had) blijft gewoon op de klok beslist.
 */
export function afterVersion(clientUpdatedAt: string, version: string | null | undefined): string {
    const own = Date.parse(clientUpdatedAt);
    const seen = version ? Date.parse(version) : Number.NaN;

    if (!Number.isFinite(seen) || !Number.isFinite(own) || own > seen) {
        return clientUpdatedAt;
    }

    return isoWithOffset(seen + 1);
}

/**
 * **De enige manier om data van een omgeving te wijzigen.** Schrijft lokaal én
 * zet de bewerkingen in de outbox, in één transactie. Features roepen dit aan
 * via een helper in `entities/*` en wachten nooit op het netwerk.
 *
 * Valt de verbinding met IndexedDB weg - Safari op een iPad doet dat soms nadat
 * de app op de achtergrond stond - dan faalt de transactie als geheel, zonder
 * iets half te schrijven. De database gaat dan één keer dicht en weer open en de
 * mutatie wordt opnieuw uitgevoerd, op de toestand van dat moment.
 */
export async function applyMutation(
    input: ApplyMutationInput,
    db: TenantDatabase = requireTenantDb(),
    now: () => number = Date.now,
): Promise<OutboxOperation[]> {
    let operations: OutboxOperation[];

    try {
        operations = await writeMutation(input, db, now);
    } catch (error) {
        if (!isLostConnection(error)) {
            throw error;
        }

        db.close({ disableAutoOpen: false });
        await db.open();
        operations = await writeMutation(input, db, now);
    }

    flusher?.();

    return operations;
}

async function writeMutation(
    input: ApplyMutationInput,
    db: TenantDatabase,
    now: () => number,
): Promise<OutboxOperation[]> {
    const createdAt = now();
    const clientUpdatedAt = isoWithOffset(createdAt);

    return db.transaction(
        'rw',
        [db.inspections, db.answers, db.multiCounts, db.findings, db.photos, db.outbox],
        async () => {
            const derived = (await input.apply?.(db, clientUpdatedAt)) ?? [];
            const queued: OutboxOperation[] = [];

            for (const operation of [...(input.operations ?? []), ...derived]) {
                const record = await enqueueOperation(db, operation, clientUpdatedAt, createdAt);

                if (record) {
                    queued.push(record);
                }
            }

            return queued;
        },
    );
}

/**
 * De fouten van een verbinding met IndexedDB die weg is, niet van een schrijfactie
 * die zelf fout is: die laatste (een volle schijf, een ingediende opname) gaat
 * gewoon door naar de aanroeper.
 */
const LOST_CONNECTION = new Set(['UnknownError', 'InvalidStateError', 'TransactionInactiveError']);

export function isLostConnection(error: unknown): boolean {
    return hasErrorName(error, LOST_CONNECTION);
}

/** Het apparaat heeft geen ruimte meer: opnieuw proberen helpt pas als er ruimte is vrijgemaakt. */
export function isStorageFull(error: unknown): boolean {
    return hasErrorName(error, new Set(['QuotaExceededError']));
}

/** Een fout met een van deze namen, ook als Dexie hem in een andere fout verpakt (`inner`). */
function hasErrorName(error: unknown, names: ReadonlySet<string>): boolean {
    for (let current = error, depth = 0; current && depth < 3; depth += 1) {
        if (typeof current !== 'object') {
            return false;
        }

        if ('name' in current && names.has(String(current.name))) {
            return true;
        }

        current = 'inner' in current ? current.inner : undefined;
    }

    return false;
}
